import { useEffect, useState } from 'react'

interface PlayerDemoProps {
  still: string
  title: string
}

export default function PlayerDemo({ still, title }: PlayerDemoProps) {
  const [playing, setPlaying] = useState(false)
  const [progress, setProgress] = useState(0)

  useEffect(() => {
    if (!playing) return
    const timer = window.setInterval(() => {
      setProgress((current) => {
        if (current >= 100) return 100
        return Math.min(100, current + 1.5)
      })
    }, 500)
    return () => window.clearInterval(timer)
  }, [playing])

  const togglePlay = () => {
    if (progress >= 100) {
      setProgress(0)
      setPlaying(true)
      return
    }
    setPlaying((value) => !value)
  }

  return (
    <div>
      <div className="relative aspect-video w-full overflow-hidden rounded-lg bg-primary-950">
        <img
          src={still}
          alt={`《${title}》播放演示画面`}
          title={`${title} - 播放演示示例`}
          className={`h-full w-full object-cover object-top transition-all duration-500 ${
            playing ? 'opacity-60' : 'opacity-40 blur-[2px]'
          }`}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950/90 via-primary-950/40 to-primary-950/60" />

        <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
          {!playing ? (
            <>
              <button
                type="button"
                onClick={togglePlay}
                aria-label="播放演示"
                className="flex h-16 w-16 cursor-pointer items-center justify-center rounded-full border border-background-50/40 bg-background-50/15 text-background-50 backdrop-blur-sm transition-colors hover:bg-accent-500 hover:text-background-50"
              >
                <i className="ri-play-fill text-2xl" />
              </button>
              <p className="mt-4 text-sm font-medium text-background-50">在线播放演示示例</p>
              <p className="mt-1 max-w-md text-xs leading-5 text-background-300">
                本区域为界面演示，暂未接入真实视频源，点击播放可查看演示交互效果。
              </p>
            </>
          ) : (
            <>
              <div className="flex items-end gap-1">
                {[0, 1, 2, 3, 4].map((bar) => (
                  <span
                    key={bar}
                    className="w-1.5 animate-soft-pulse rounded-full bg-accent-400"
                    style={{
                      height: `${14 + ((bar * 7) % 22)}px`,
                      animationDelay: `${bar * 0.18}s`,
                    }}
                  />
                ))}
              </div>
              <p className="mt-4 text-sm font-medium text-background-50">演示播放中</p>
              <p className="mt-1 text-xs text-background-300">
                未接入真实视频，进度条仅用于展示播放器界面
              </p>
              <button
                type="button"
                onClick={togglePlay}
                className="mt-4 cursor-pointer rounded-md border border-background-50/40 px-4 py-1.5 text-xs text-background-100 transition-colors hover:border-accent-400 hover:text-accent-300"
              >
                <i className="ri-pause-line mr-1" />
                暂停演示
              </button>
            </>
          )}
        </div>

        <div className="absolute inset-x-0 bottom-0 px-3 pb-3 md:px-4 md:pb-4">
          <div className="h-1 w-full overflow-hidden rounded-full bg-background-50/25">
            <div
              className="h-full rounded-full bg-accent-500 transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="mt-2 flex items-center justify-between text-[10px] text-background-300">
            <span className="font-label tracking-wide">
              {playing ? '正在演示' : '已暂停'} · {Math.round(progress)}%
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-hd-line" />
              演示界面
            </span>
          </div>
        </div>
      </div>

      <div className="mt-3 flex flex-col gap-2 rounded-md border border-accent-200 bg-accent-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="flex items-start gap-2 text-xs leading-5 text-accent-900">
          <i className="ri-error-warning-line mt-0.5 text-sm text-accent-700" />
          播放区域为示例演示，当前站点未接入任何真实视频播放服务，请勿据此判断片源可用性。
        </p>
        <span className="whitespace-nowrap rounded-full bg-accent-100 px-2.5 py-1 text-[10px] text-accent-800">
          DEMO ONLY
        </span>
      </div>
    </div>
  )
}